"use client";

import useSWR from "swr";
import { useSWRConfig } from "swr";
import { NOTIFICATIONS_UNREAD_COUNT_KEY } from "@/lib/notifications/cache";
import { getValidSupabaseAccessToken } from "@/lib/supabase-access-token";

type UnreadCountResponse = { unreadCount: number };

async function fetchUnreadCount(): Promise<number> {
  const accessToken = await getValidSupabaseAccessToken();
  if (!accessToken) {
    return 0;
  }

  const res = await fetch("/api/notifications/inbox?count_only=true", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!res.ok) {
    throw new Error("Failed to fetch unread notification count");
  }

  const data = (await res.json()) as UnreadCountResponse;
  return data.unreadCount ?? 0;
}

export function useNotificationUnreadCount(enabled: boolean, _userId?: string) {
  const { cache } = useSWRConfig();
  const cachedUnread = cache.get(NOTIFICATIONS_UNREAD_COUNT_KEY) as
    | number
    | undefined;

  const { data, error, isLoading, mutate } = useSWR(
    enabled ? NOTIFICATIONS_UNREAD_COUNT_KEY : null,
    fetchUnreadCount,
    {
      fallbackData: cachedUnread,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      revalidateIfStale: false,
      dedupingInterval: 30_000,
    },
  );

  // Realtime invalidation for NOTIFICATIONS_UNREAD_COUNT_KEY is handled once by
  // NotificationUserRealtimeSubscriber (see AppProviders).

  return {
    unread: data ?? 0,
    isLoading,
    error: error instanceof Error ? error : null,
    refresh: mutate,
  };
}
